/**
 * App creation-limit resolution — single source of truth for "how many of this
 * app may a team create, and which warning to show when they hit the cap".
 *
 * Unifies what used to be two parallel code paths in useOpenAppModal:
 *  - Virtual Screen cap (company.feature.virtualScreenCountLimit)
 *  - Generic limitationCreateAsset cap (AppMeta.func.params, scio1 parity)
 *
 * `resolveCreationLimit()` is pure (no Apollo/React) → unit-testable in isolation.
 * `checkCreationLimit()` adds the async asset count via an injected Apollo client
 * (dependency inversion — the count source is a parameter, not a hard import on
 * a hook).
 *
 * Open/Closed: adding a new "simple cap" app needs NO code change here — set
 * `limitationCreateAsset` (and optionally `keyLimitationCreateAsset`) on the
 * app's AppMeta document. Only an app with a genuinely custom rule (different
 * cap source or count query) needs a new clause in `resolveCreationLimit()`.
 *
 * scio1 refs: appManagementComponent.js:86-112 (virtualScreen) + 124-131 (generic).
 */
import type { ApolloClient } from '@apollo/client';
import { ASSETS_COUNT_QUERY } from '@scio3/graphql-client';
import type { AppMeta, AppMetaParams, CompanyInfo } from '@scio3/shared-types';
import { APP_TYPES } from '@scio3/shared-types';

/** Which warning copy + title the limit dialog renders. */
export type CreationLimitKind =
  | 'virtualScreen'
  | 'emergencyFeed'
  | 'limitationCreateAsset'
  | 'limitationCreateAssetEnterprise';

/**
 * Counting scope for the cap.
 *  - `team`    → count `{appType, accountId, teamId}` (active team only).
 *  - `account` → count `{appType, accountId}` (account-wide, all teams).
 *
 * Matches scio1: Emergency Feed is enforced account-wide by its server hook;
 * Virtual Screen + Hosted Website + every generic/future app are counted in
 * client Minimongo (active-team-only). `team` is the default. See
 * claude-dev/2026-06-29-hosted-website-creation-limit/03-init-…md decision 1.
 */
export type CreationLimitScope = 'account' | 'team';

/** Cap + presentation, resolved before the live count is known. */
export interface CreationLimitRule {
  /** Effective cap; `null` (or <= 0) means unlimited — no gate. */
  limit: number | null;
  kind: CreationLimitKind;
  /** Whether the count is account-wide or active-team-only. */
  scope: CreationLimitScope;
  /** "Learn more" link — only used by the enterprise copy. */
  helpLink?: string;
}

/** Everything the limit dialog needs once a team has hit the cap. */
export interface CreationLimitNotice {
  /** Display name of the limited app (e.g. "Hosted Website"). */
  appName: string;
  /** Effective cap (rendered as maxNumber). */
  limit: number;
  /** How many of this app the team already owns (rendered as currentNumber). */
  count: number;
  kind: CreationLimitKind;
  helpLink?: string;
}

/**
 * Resolve the creation cap for an app + company. Pure — no I/O.
 *
 * Cap precedence (MUST stay identical to the open-api copy —
 * `optisigns-open-api/src/app/asset/app-creation-limit.ts` — and 1:1 with scio1):
 *  1. virtualScreen → typeof feature.virtualScreenCountLimit === 'number' ? it : 1   scope=team
 *  2. emergencyFeed  → featureData.emergencyFeedLimit || 50                           scope=account
 *  3. generic (incl. hostedWebsite) → featureData[keyLimitationCreateAsset] (truthy)
 *       else func.params.limitationCreateAsset, else null   kind=limitationCreateAsset*  scope=team
 *  Block when limit != null AND count >= limit (limit 0 is a real cap — VS=0 blocks all).
 *
 * Only virtualScreen + emergencyFeed have app-specific clauses; everything else
 * (incl. hostedWebsite) is data-driven via AppMeta func.params, team-scoped — exactly
 * like scio1 (appManagementComponent.js:90-91 + 123-131; emergencyFeedSchema.js:25).
 * scio1 has NO hostedWebsite-specific clause: it reads featureData.hostedWebsiteLimitation
 * only because that app's AppMeta sets func.params.keyLimitationCreateAsset='hostedWebsiteLimitation'.
 */
export function resolveCreationLimit(app: AppMeta, company: CompanyInfo | null): CreationLimitRule {
  const featureData = (company?.featureData as Record<string, unknown> | undefined) ?? {};

  // 1. virtualScreen — scio1 appManagementComponent.js:90-91: the company value
  //    is the cap ONLY when it's a number; any non-number (absent/false) → 1.
  //    Team-scoped (scio1 counts the active team's Minimongo).
  if (app.appType === APP_TYPES.VIRTUAL_SCREEN) {
    const v = company?.feature?.virtualScreenCountLimit;
    return { limit: typeof v === 'number' ? v : 1, kind: 'virtualScreen', scope: 'team' };
  }

  // 2. emergencyFeed — scio1 emergencyFeedSchema.js:25: `featureData.emergencyFeedLimit || 50`
  //    (any falsy value → 50). Account-scoped (server hook counts {appType, accountId}).
  if (app.appType === APP_TYPES.EMERGENCY_FEED) {
    return {
      limit: (featureData.emergencyFeedLimit as number) || 50,
      kind: 'emergencyFeed',
      scope: 'account',
    };
  }

  // 3. Generic (incl. hostedWebsite) — scio1 appManagementComponent.js:123-131.
  //    Effective cap = featureData[keyLimitationCreateAsset] when truthy, else
  //    func.params.limitationCreateAsset; null (no cap) when neither. Team-scoped.
  //    hostedWebsite reads featureData.hostedWebsiteLimitation IFF its AppMeta sets
  //    func.params.keyLimitationCreateAsset='hostedWebsiteLimitation' (data — scio1
  //    has no hostedWebsite-specific clause). func.params can be a string at runtime → {}.
  const params: AppMetaParams =
    app.func?.params && typeof app.func.params === 'object' ? app.func.params : {};
  const overrideKey = params.keyLimitationCreateAsset;
  const override = overrideKey ? featureData[overrideKey] : undefined;
  const limit = (override || params.limitationCreateAsset || null) as number | null;

  return {
    limit,
    kind:
      params.requiredPlan === 'enterprise'
        ? 'limitationCreateAssetEnterprise'
        : 'limitationCreateAsset',
    scope: 'team',
    helpLink: params.helpLink,
  };
}

/**
 * Resolve the cap, then (only if capped) count the team's existing assets of
 * this type and return a notice when the cap is reached. `null` = allowed.
 *
 * Fail-open on count error — scio1 parity (Meteor reactive count never throws);
 * a transient count failure must not block legitimate creation.
 */
export async function checkCreationLimit(
  app: AppMeta,
  company: CompanyInfo | null,
  apolloClient: ApolloClient,
  teamId: string | null | undefined,
): Promise<CreationLimitNotice | null> {
  const { limit, kind, scope, helpLink } = resolveCreationLimit(app, company);

  // null = no cap (scio1 parity). A numeric cap (incl. 0) is real — block when
  // count >= limit (VS=0 blocks all). Do NOT treat 0 as unlimited.
  if (limit == null || !app.appType) return null;

  try {
    // Scope the count: team caps pass teamId (active team only); account caps
    // omit teamId so the backend `assets` query counts account-wide (all teams).
    // The backend guard is authoritative — this pre-check is best-effort UX.
    const { data } = await apolloClient.query<{
      assets?: { pageData?: { total?: number } };
    }>({
      query: ASSETS_COUNT_QUERY,
      variables: {
        teamId: scope === 'team' ? teamId : undefined,
        query: { appType: app.appType },
      },
      fetchPolicy: 'network-only',
    });
    const count = data?.assets?.pageData?.total ?? 0;
    if (count >= limit) {
      return { appName: app.text ?? app.appType, limit, count, kind, helpLink };
    }
  } catch {
    // Fail-open — see jsdoc.
  }
  return null;
}

/**
 * Map an Apollo/GraphQL error into a CreationLimitNotice when (and only when)
 * it carries the backend's `CREATION_LIMIT_EXCEEDED` code. Returns `null` for
 * every other error so callers fall back to their normal error handling.
 *
 * This is the save-path echo of the pre-check: when a create slips past the
 * client pre-check (clone, SmallApp-editor save, AI tool, …) the backend guard
 * throws this error and the same global `CreationLimitDialog` should render.
 *
 * Error contract (spec §3) — GraphQL error extensions:
 *   code: 'CREATION_LIMIT_EXCEEDED'
 *   extensions: { appType, limit, count, kind }
 *
 * `appNameLookup` resolves a display name from the appType (e.g. AppMeta.text);
 * falls back to the appType string when absent.
 */
export function mapCreationLimitError(
  error: unknown,
  appNameLookup?: (appType: string) => string | undefined,
): CreationLimitNotice | null {
  const err = error as {
    graphQLErrors?: Array<{ extensions?: Record<string, unknown> }>;
    errors?: Array<{ extensions?: Record<string, unknown> }>;
  };
  const ext = (err?.errors?.[0] ?? err?.graphQLErrors?.[0])?.extensions;
  if (!ext || ext.code !== 'CREATION_LIMIT_EXCEEDED') return null;

  const appType = typeof ext.appType === 'string' ? ext.appType : '';
  const limit = typeof ext.limit === 'number' ? ext.limit : 0;
  const count = typeof ext.count === 'number' ? ext.count : 0;
  const kind = (ext.kind as CreationLimitKind) ?? 'limitationCreateAsset';

  return {
    appName: appNameLookup?.(appType) ?? appType,
    limit,
    count,
    kind,
  };
}
